import { useState, useEffect } from "react";
import { api } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import {
  MagnifyingGlass,
  Plus,
  Trash,
  PencilSimple,
  MapTrifold,
  MapPin,
} from "@phosphor-icons/react";

const CURRENCIES = ["USD", "EUR", "GBP", "INR", "AED", "AUD", "SGD", "THB", "MYR", "JPY", "IDR"];

const emptyForm = {
  name: "",
  city: "",
  description: "",
  duration: "",
  default_cost: 0,
  default_currency: "USD",
  pickup: "",
  vendor_name: "",
  vendor_contact: "",
  vendor_cost: 0,
};

const TourLibraryPage = () => {
  const [tours, setTours] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showDialog, setShowDialog] = useState(false);
  const [editingTour, setEditingTour] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [form, setForm] = useState({ ...emptyForm });

  const loadTours = async () => {
    try {
      const res = await api.get(`/tours?search=${search}`);
      setTours(res.data);
    } catch {
      toast.error("Failed to load tours");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadTours(); }, [search]);

  const openNew = () => {
    setEditingTour(null);
    setForm({ ...emptyForm });
    setShowDialog(true);
  };

  const openEdit = (tour) => {
    setEditingTour(tour);
    setForm({
      name: tour.name,
      city: tour.city,
      description: tour.description,
      duration: tour.duration,
      default_cost: tour.default_cost,
      default_currency: tour.default_currency,
      pickup: tour.pickup,
      vendor_name: tour.vendor_name,
      vendor_contact: tour.vendor_contact,
      vendor_cost: tour.vendor_cost,
    });
    setShowDialog(true);
  };

  const saveTour = async () => {
    if (!form.name.trim()) {
      toast.error("Tour name is required");
      return;
    }
    try {
      if (editingTour) {
        await api.put(`/tours/${editingTour.id}`, form);
        toast.success("Tour updated");
      } else {
        await api.post("/tours", form);
        toast.success("Tour saved to library");
      }
      setShowDialog(false);
      loadTours();
    } catch {
      toast.error("Failed to save tour");
    }
  };

  const deleteTour = async () => {
    try {
      await api.delete(`/tours/${deleteId}`);
      toast.success("Tour deleted");
      setDeleteId(null);
      loadTours();
    } catch {
      toast.error("Failed to delete");
    }
  };

  const update = (field, value) => setForm((f) => ({ ...f, [field]: value }));

  return (
    <div className="max-w-5xl mx-auto" data-testid="tour-library-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-gold mb-1">Tour Library</p>
          <h1 className="font-serif text-3xl text-navy">Saved Tours & Activities</h1>
        </div>
        <Button onClick={openNew} className="bg-navy hover:bg-navy/90 text-white gap-2" data-testid="add-tour-btn">
          <Plus size={16} /> Add Tour
        </Button>
      </div>

      <div className="relative mb-4">
        <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search tours by name or city..."
          className="pl-9 bg-white border-border"
          data-testid="tour-search"
        />
      </div>

      {loading ? (
        <p className="text-muted-foreground text-sm py-8 text-center">Loading...</p>
      ) : tours.length === 0 ? (
        <div className="text-center py-16 bg-white border border-border">
          <MapTrifold size={40} className="mx-auto text-muted-foreground/30 mb-3" />
          <p className="text-muted-foreground text-sm">No tours saved yet</p>
          <p className="text-muted-foreground/60 text-xs mt-1">Add tours and activities to reuse across proposals</p>
        </div>
      ) : (
        <div className="bg-white border border-border divide-y divide-border">
          {tours.map((tour) => (
            <div key={tour.id} className="flex items-center justify-between p-4 hover:bg-cream/30 transition-colors" data-testid={`tour-item-${tour.id}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5">
                  <span className="font-serif text-base text-navy font-bold">{tour.name}</span>
                  {tour.default_cost > 0 && (
                    <span className="text-xs text-gold font-semibold">{tour.default_currency} {tour.default_cost.toLocaleString()}</span>
                  )}
                </div>
                <div className="flex items-center gap-3 text-xs text-muted-foreground">
                  {tour.city && <span className="flex items-center gap-1"><MapPin size={11} /> {tour.city}</span>}
                  {tour.duration && <span>{tour.duration}</span>}
                  {tour.vendor_name && <span className="text-gold/70">Vendor: {tour.vendor_name}</span>}
                </div>
              </div>
              <div className="flex gap-1 shrink-0">
                <Button variant="ghost" size="sm" onClick={() => openEdit(tour)} className="h-8 w-8 p-0 text-muted-foreground hover:text-navy">
                  <PencilSimple size={14} />
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setDeleteId(tour.id)} className="h-8 w-8 p-0 text-muted-foreground hover:text-destructive">
                  <Trash size={14} />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle className="font-serif text-navy">{editingTour ? "Edit Tour" : "Add Tour to Library"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 max-h-[60vh] overflow-y-auto py-2">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Tour/Activity Name *</Label>
                <Input value={form.name} onChange={(e) => update("name", e.target.value)} placeholder="e.g. Ubud Rice Terraces Tour" className="bg-white border-border" data-testid="tour-name-input" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">City</Label>
                <Input value={form.city} onChange={(e) => update("city", e.target.value)} placeholder="e.g. Bali" className="bg-white border-border" />
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Description</Label>
              <Textarea value={form.description} onChange={(e) => update("description", e.target.value)} placeholder="Tour highlights, what's included..." className="bg-white border-border min-h-[60px]" />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Duration</Label>
                <Input value={form.duration} onChange={(e) => update("duration", e.target.value)} placeholder="e.g. 4 hours" className="bg-white border-border" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Client Cost</Label>
                <Input type="number" value={form.default_cost || ""} onChange={(e) => update("default_cost", parseFloat(e.target.value) || 0)} placeholder="0" className="bg-white border-border" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Currency</Label>
                <Select value={form.default_currency} onValueChange={(v) => update("default_currency", v)}>
                  <SelectTrigger className="bg-white border-border"><SelectValue /></SelectTrigger>
                  <SelectContent>{CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Pickup Details</Label>
              <Input value={form.pickup} onChange={(e) => update("pickup", e.target.value)} placeholder="e.g. Hotel lobby at 8 AM" className="bg-white border-border" />
            </div>

            <div className="border-t border-border pt-3 mt-3">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-gold mb-2">Vendor Details (internal only — not shown to client)</p>
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Vendor Name</Label>
                  <Input value={form.vendor_name} onChange={(e) => update("vendor_name", e.target.value)} placeholder="Supplier name" className="bg-white border-border" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Vendor Contact</Label>
                  <Input value={form.vendor_contact} onChange={(e) => update("vendor_contact", e.target.value)} placeholder="Phone / email" className="bg-white border-border" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Vendor Cost</Label>
                  <Input type="number" value={form.vendor_cost || ""} onChange={(e) => update("vendor_cost", parseFloat(e.target.value) || 0)} placeholder="0" className="bg-white border-border" />
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
            <Button onClick={saveTour} className="bg-navy hover:bg-navy/90 text-white" data-testid="save-tour-btn">{editingTour ? "Update" : "Save"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Tour?</AlertDialogTitle>
            <AlertDialogDescription>This will remove the tour from your library permanently.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={deleteTour} className="bg-destructive text-destructive-foreground">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default TourLibraryPage;
